import { css, useTheme } from "@emotion/react";
import { UpsunTheme } from "@upsun/react-component-library/src/themes";

import Button from "@upsun/react-component-library/src/components/Button";
import GridCol from "@upsun/react-component-library/src/components/GridCol";
import GridContainer from "@upsun/react-component-library/src/components/GridContainer";
import Heading from "@upsun/react-component-library/src/components/Heading";
import TextWithHighlight from "@upsun/react-component-library/src/components/TextWithHighlight";

import { 
  mediaQueries,
  pxToRem,
  respSpacing,
  spacing,
} from "@upsun/react-component-library/src/styles";
import AnnouncementPill from "@upsun/react-component-library/src/components/AnnouncementPill";

const HomepageHero: React.FC<{ locale?: string }> = ({ locale = "en" }) => {
  const theme = useTheme() as UpsunTheme;

  const containerStyles = css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    position: relative;

    ${theme.name === "light"
      ? `
        background:
          linear-gradient(
            180deg,
            rgba(247,248,248,1) 0%,
            rgba(255,255,255,0.01) 66%,
            rgba(255,255,255,0.02) 100%
          ),
          linear-gradient(
            53deg,
            rgba(223, 218, 255, 1) 0%,
            rgba(253, 232, 253, 1) 34%,
            rgba(255, 237, 231, 1) 67%,
            rgba(247, 254, 204, 1) 100%
          );
    `
      : `
        background:
          linear-gradient(
            0deg,
              #191C1EFF 0%,
              #191C1E01 100%
            );
      `};

    padding: ${pxToRem(spacing.mobile["2xl"])} ${pxToRem(spacing.mobile["xl"])};
    margin-bottom: ${pxToRem(spacing.mobile["2xl"])};

    ${mediaQueries.md} {
      padding: ${pxToRem(spacing.tablet["2xl"])} ${pxToRem(spacing.tablet["xl"])};
      margin-bottom: ${pxToRem(spacing.tablet["2xl"])};
    }

    ${mediaQueries.xl} {
      padding: ${pxToRem(spacing.desktop["2xl"])} ${pxToRem(spacing.desktop["xl"])};
      margin-bottom: ${pxToRem(spacing.desktop["2xl"])};
    }
  `;

  const subtitleStyles = css`
    margin-top: ${pxToRem(spacing.mobile.s)};
    margin-bottom: ${pxToRem(spacing.mobile.sm)};

    ${mediaQueries.md} {
      margin-top: ${pxToRem(spacing.tablet.s)};
      margin-bottom: ${pxToRem(spacing.tablet.sm)};
    }

    ${mediaQueries.xl} {
      margin-top: ${pxToRem(spacing.desktop.s)};
      margin-bottom: ${pxToRem(spacing.desktop.sm)};
    }
  `;

  const playPauseVideo = (event: React.MouseEvent<HTMLVideoElement>) => {
    const video: HTMLVideoElement = event.target as HTMLVideoElement;

    video?.paused ? video?.play() : video?.pause();
  };

  const maxWidth = css`
    max-width: 1000px;
  `;

  const pillStyles = css`
    display: inline-block;
    ${respSpacing('margin-block-end','2xl')}
  `;
  const title: { [key: string]: string } = {
    en: `Deliver every release`,
    fr: `Livrez chaque version`,
    de: `Jede Veröffentlichung`,
  };

  const certainty: { [key: string]: string } = {
    en: "_with certainty_",
    fr: "_en toute confiance_",
    de: "_mit Sicherheit bereitstellen_",
  }

  const subtitle: { [key: string]: string } = {
    en: "The cloud application platform that instantly spins up production-perfect clones—live data, config, and code—on any branch, across AWS, Azure, or GCP. Stop testing with mocks and stale data. Eliminate staging drift, accelerate signoff, and launch with confidence—because when it’s real, it just works.",
    fr: "La plateforme d'applications cloud qui génère instantanément des clones parfaits de la production (copie de données temps réels, configuration et code) sur n'importe quelle branche, sur AWS, Azure ou GCP. Ne testez plus avec des simulations et des données obsolètes. Éliminez les décalages de mise en scène, accélérez la validation et lancez-vous en toute confiance, car quand c'est réel, ça fonctionne tout simplement.",
    de: "Die Cloud-Anwendungsplattform, die sofort produktionsreife Klone inklusive Live-Daten, Konfigurationen und Code, auf jedem Zweig, über AWS, Azure oder GCP hinweg erstellt. Schluss mit Tests mit Mocks und veralteten Daten. Eliminieren Sie Staging-Drift, beschleunigen Sie die Freigabe und starten Sie mit Zuversicht, denn wenn es echt ist, funktioniert es einfach.",
  };

  const buttonText: { [key: string]: string } = {
    en: "Start your free trial",
    fr: "Commencez votre essai gratuit",
    de: "Starten Sie Ihre kostenlose Testversion",
  };

  return (
    <div css={containerStyles}>
      <GridContainer>
        <GridCol
          colStart={1}
          colEnd={13}
          colStartMd={2}
          colEndMd={12}
          css={css`
            display: flex;
            align-items: center;
            flex-direction: column;
          `}
        >
          <div css={maxWidth}>
            <div css={pillStyles}><AnnouncementPill text={`Platform.sh is now Upsun. <a href="/platform-sh-is-now-upsun/">Read more</a>`} /></div>
            <Heading level={1}>
              {title[locale]}<br/><TextWithHighlight orig={certainty[locale]}/>
            </Heading>
            <p css={subtitleStyles}>{subtitle[locale]}</p>
            <Button
              level="primary"
              text={buttonText[locale]}
              href={"https://auth.upsun.com/register/"}
            />
          </div>
        </GridCol>
      </GridContainer>
    </div>
  );
};

export default HomepageHero;
